import { Theme, useTheme } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import clsx from "clsx";
import { handleKeyDown } from "core/model/accessibility/keyboardActions";
import { TourElement } from "core/types";
import { BadgeContainer, NewBadge } from "ds_legacy/components/Badge";
import { Circle } from "ds_legacy/components/Circles";
import Tooltip from "ds_legacy/components/Tooltip";
import { ACCENT_COLOR } from "ds_legacy/materials/colors";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { dp, margin } from "ds_legacy/materials/metrics";
import { Tab as TabText } from "ds_legacy/materials/typography";
import {
  CSSProperties,
  Children,
  ComponentProps,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
} from "react";
import { Link, useLocation } from "react-router-dom";

type TabsBaseProps = {
  as?: React.ElementType;
  children: ReactNode;
  containerAs?: React.ElementType;
  containerStyle?: CSSProperties;
  height?: CSSProperties["height"];
  isFit?: boolean;
  minHeight?: CSSProperties["minHeight"];
  minWidth?: CSSProperties["minWidth"];
  tabsContainerPadding?: CSSProperties["padding"];
};

type TabsProps = TabsBaseProps &
  (
    | { label: string; labelledBy?: never }
    | { label?: never; labelledBy: string }
    | { label?: never; labelledBy?: never }
  );

const getColor = ({
  disabled,
  primary,
  selected,
  theme,
}: {
  disabled: boolean | undefined;
  primary: boolean | undefined;
  selected: boolean | undefined;
  theme: Theme;
}) => {
  if (primary && disabled) {
    return theme.palette.grey[600];
  }
  if (primary && selected) {
    return theme.palette.primary.main;
  }
  if (primary) {
    return theme.palette.common.black;
  }
  return undefined;
};

const LinkComponent = forwardRef<
  HTMLAnchorElement,
  ComponentProps<typeof Link>
>((linkProps, ref) => <Link {...linkProps} ref={ref} />);

export function Tab({
  children,
  fontSize,
  id,
  isCustom,
  isDisabled,
  isPrimary = false,
  isSelected,
  isUppercase = true,
  label,
  lineHeight,
  match,
  minWidth,
  onClick = () => {},
  shouldDisableTouchRipple,
  shouldShowBadge,
  shouldShowNotification,
  shouldWrapLabel = false,
  tabMargin,
  tabPadding,
  testId,
  tooltip,
  tour,
  url,
}: {
  children?: (b: boolean) => ReactNode;
  fontSize?: CSSProperties["fontSize"];
  id?: string;
  isCustom?: boolean;
  isDisabled?: boolean;
  isPrimary?: boolean;
  isSelected?: boolean;
  isUppercase?: boolean;
  label?: string;
  lineHeight?: CSSProperties["lineHeight"];
  match: string;
  minWidth?: CSSProperties["minWidth"];
  onClick?: () => void;
  shouldDisableTouchRipple?: boolean;
  shouldShowBadge?: boolean;
  shouldShowNotification?: boolean;
  shouldWrapLabel?: boolean;
  tabMargin?: CSSProperties["margin"];
  tabPadding?: CSSProperties["padding"];
  testId?: string;
  tooltip?: string;
  tour?: TourElement;
  url?: string;
}) {
  const location = useLocation();
  const theme = useTheme();

  const isLink = url != null;

  const selected =
    (location?.pathname && location.pathname.search(new RegExp(match)) >= 0) ||
    isSelected ||
    false;
  // needed for ie 11 (My Account Tab)
  if (isCustom && children) return <>{children(selected)}</>;

  const handleClick = () => (isDisabled ? null : onClick());

  const baseComponentProps = isLink
    ? { onClick: handleClick, to: url }
    : {
        disableTouchRipple: shouldDisableTouchRipple,
        focusRipple: true,
        onClick: handleClick,
        onKeyDown: handleKeyDown({ onConfirm: handleClick }),
      };

  return (
    <Tooltip title={tooltip}>
      <div
        className={clsx("relative flex flex-1 flex-col items-stretch", {
          "cursor-pointer": !isDisabled,
        })}
        style={{ margin: tabMargin ?? undefined }}
      >
        <ButtonBase
          {...tour}
          aria-current={selected ? "page" : undefined}
          component={isLink ? LinkComponent : "div"}
          data-testid={testId}
          disabled={isDisabled}
          id={id}
          {...baseComponentProps}
          style={{
            display: "flex",
            flex: `1 1 auto`,
            height: "100%",
            minWidth,
            outlineOffset: dp(-1),
            padding: tabPadding,
            textAlign: "center",
            width: "100%",
          }}
        >
          <div
            className={clsx("flex flex-1 items-center justify-center", {
              "flex-col": children === undefined,
              "flex-row": children !== undefined,
            })}
          >
            {children?.(selected) || (
              <BadgeContainer>
                <HorizontalLayout aligned>
                  <TabText
                    as="p"
                    color={getColor({
                      disabled: isDisabled,
                      primary: isPrimary,
                      selected,
                      theme,
                    })}
                    fontSize={fontSize}
                    light={selected}
                    lineHeight={lineHeight}
                    selected={selected}
                    style={{
                      whiteSpace: "normal",
                      wordBreak: shouldWrapLabel ? "break-word" : undefined,
                    }}
                    uppercase={isUppercase}
                  >
                    {label}
                  </TabText>
                  {shouldShowNotification && (
                    <Circle
                      className="absolute top-0 right-[-16px]"
                      color={ACCENT_COLOR}
                    />
                  )}
                  {shouldShowBadge && (
                    <NewBadge marginWrapper={margin(0, 0, 0, 1)} />
                  )}
                </HorizontalLayout>
              </BadgeContainer>
            )}
          </div>
        </ButtonBase>
        {selected && (
          <hr
            className={clsx(
              "absolute right-0 bottom-0 left-0 m-0 h-0.5 border-0 p-0",
              {
                "bg-primary": isPrimary,
                "bg-white": !isPrimary,
              },
            )}
          />
        )}
      </div>
    </Tooltip>
  );
}

export default function Tabs({
  as,
  children,
  containerAs,
  containerStyle,
  height,
  isFit,
  label,
  labelledBy,
  minHeight,
  minWidth,
  tabsContainerPadding,
}: TabsProps) {
  const TabsWrapperComponent = as ?? "div";
  const TabsContainerComponent = containerAs ?? "div";

  return (
    <TabsWrapperComponent
      aria-label={label}
      aria-labelledby={labelledBy}
      className="flex flex-col items-stretch"
      style={{ padding: tabsContainerPadding }}
    >
      <TabsContainerComponent
        className={clsx("relative flex flex-row items-stretch justify-center", {
          "w-[280px]": !isFit,
          "w-auto": isFit,
        })}
        style={{
          height: height ?? "48px",
          minHeight: minHeight ?? "48px",
          minWidth: minWidth ?? "72px",
          ...containerStyle,
        }}
      >
        {Children.map(children, (child) =>
          isValidElement(child) ? cloneElement(child) : child,
        )}
      </TabsContainerComponent>
    </TabsWrapperComponent>
  );
}
